import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import root from 'react-shadow';
import { Button, PoweredByMyinterview } from '@myinterview/component-library';
import { IntercomProvider } from 'react-use-intercom';
import { detectIncognito } from 'detect-incognito';
import classNames from 'classnames';
import { IOnFinish, IOnError, IClientWidgetConfig, IWidgetRef } from '../interfaces/configInterface';
import { Main } from './Main';
import styles from '../assets/styles/global.styles.scss';
import { RotateScreen } from './ui/RotateScreen';
import { ErrorBoundary } from './ErrorBoundaries';
import configFile from '../config';
import { UnsupportedModal } from './ui/UnsupportedModal';
import { isAndroid, isMobile, isSupportedDevice, isSupportedIosDevice } from '../utils/device.utils';
import { updateVideoAxiosBaseURL } from '../services/axiosInstances.service';
import { FONT_URL } from '../utils/constants.utils';
import { convertQuestions } from '../utils/convertor.utils';
import { emitTrackEvent, EVENT_TYPES } from '../services/event.service';
import { useAccessibility } from '../hooks/useAccessibility';

const DivComponent = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(({ children, ...props }, ref) => <div ref={ref} {...props}>{children}</div>);

export const Widget = forwardRef<IWidgetRef, IClientWidgetConfig>(({
  candidate,
  job,
  video,
  config,
  disabled = false,
  buttonText = 'START INTERVIEW',
  buttonStyle = {},
  children,
  styleUrls = [],
  fontsUrls = [],
}, clientRef) => {
  const widget_version = process.env.REACT_APP_RELEASE;
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);
  const myinterviewRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [shouldShowWaterMark, setShouldShowWaterMark] = useState(false);
  const [isCustomStyleProvided, setIsCustomStyleProvided] = useState(false);
  const { setSitekey, shutdown } = useAccessibility();

  useEffect(() => {
    if (isAndroid) {
      detectIncognito().then((res) => {
        (setIsIncognitoMode(res.browserName === 'Chrome' && res.isPrivate && isMobile));
      });
    }

    if (myinterviewRef.current && myinterviewRef.current.shadowRoot) {
      // @ts-ignore
      setIsCustomStyleProvided(typeof children === 'object' && children.type === 'style');

      (Array.isArray(fontsUrls) ? fontsUrls : [fontsUrls]).concat(FONT_URL).forEach((fontUrl) => {
        const fontsEl = document.createElement('link');
        fontsEl.rel = 'stylesheet';
        fontsEl.href = fontUrl;
        document.head.appendChild(fontsEl);
      });
    }

    return () => {
      shutdown();
    };
  }, []);

  const customStyleUrls = useMemo(() => (Array.isArray(styleUrls) ? styleUrls : [styleUrls]).map((styleUrl) => (
    <style key={styleUrl}>
      @import url(
      {styleUrl}
      )
    </style>
  )), [styleUrls]);

  useEffect(() => {
    if (config.env) {
      updateVideoAxiosBaseURL(configFile(config.env).serverUrl);
    }
  }, [config.env]);

  const setLayoutStyleProperties = () => {
    if (mainRef.current && buttonRef.current) {
      const {
        x, y, width, height,
      } = buttonRef.current.getBoundingClientRect();
      mainRef.current.style.setProperty('--myinterview-widget-layout-top', `${y}px`);
      mainRef.current.style.setProperty('--myinterview-widget-layout-right', `${window.innerWidth - x - width}px`);
      mainRef.current.style.setProperty('--myinterview-widget-layout-bottom', `${window.innerHeight - y - height}px`);
      mainRef.current.style.setProperty('--myinterview-widget-layout-left', `${x}px`);
      if (config.overlay) {
        mainRef.current.style.setProperty('--color-primary', config.overlay);
        mainRef.current.style.setProperty('--color-special', config.overlay);
      }
    }
  };

  const revertBodyStyling = () => {
    document.body.style.maxHeight = 'auto';
    document.body.style.overflow = 'visible';
  };

  const onCloseWidget = () => {
    revertBodyStyling();
    setIsWidgetOpen(false);
  };

  const onInterviewCompleted = async (data: IOnFinish) => {
    onCloseWidget();
    await config.onFinish?.(data);
  };

  const onError = (data: IOnError) => {
    onCloseWidget();
    config.onError?.(data);
  };

  const openWidget = () => {
    config.onWidgetClicked?.();
    if (!config.auth) return;
    setLayoutStyleProperties();
    document.body.style.maxHeight = '100vh';
    document.body.style.overflow = 'hidden';
    setIsWidgetOpen(true);
    setIsWidgetMinimized(false);
    setSitekey(config.accessibility_id || '');
  };

  const onMinimize = () => {
    setLayoutStyleProperties();
    revertBodyStyling();
    setIsWidgetMinimized(true);
    config.onWidgetClose?.();
    emitTrackEvent({ eventType: EVENT_TYPES.WIDGET_MINIMIZED });
    shutdown();
  };

  useImperativeHandle(clientRef, () => ({
    open: () => buttonRef.current?.click(),
    close: onMinimize,
  }), []);

  const layoutClassNames = classNames({
    'myinterview-widget__layout': true,
    'myinterview-widget-no-select': true,
    'myinterview-widget__layout--animated': isWidgetOpen && config.openAnimation !== false,
    'myinterview-widget__layout--minimized': isWidgetMinimized,
  });

  const wrapperClassNames = classNames({
    'myinterview-widget__wrapper': true,
    'myinterview-widget__wrapper--hide-header': !!config.hideHeader,
  });

  const WrapperComponent = useMemo(() => (config.useLightDom ? DivComponent : root.div), [config.useLightDom]);

  return (
    <WrapperComponent ref={myinterviewRef} className="myinterview-widget-client">
      <style>{styles}</style>
      {isCustomStyleProvided && children}
      {customStyleUrls}
      <ErrorBoundary>
        <IntercomProvider appId={configFile(config.env).INTERCOM_APP_ID}>
          <div
            className="myinterview-widget"
            ref={mainRef}
          >
            <Button
              ref={buttonRef}
              className="myinterview-widget__button"
              onClick={openWidget}
              disabled={disabled}
              style={buttonStyle}
            >
              {buttonText}
            </Button>

            {isWidgetOpen && (
            <div className={layoutClassNames}>
              <div className="myinterview-widget__background full">
                {shouldShowWaterMark && (
                <div className="myinterview-widget__watermark color--white">
                  <PoweredByMyinterview />
                </div>
                )}
                <div className="myinterview-widget__background-dots" />
              </div>

              {isMobile && <RotateScreen />}

              {(!isSupportedDevice || isIncognitoMode) && <UnsupportedModal messageType={!isSupportedIosDevice ? 'IOS' : !isSupportedDevice ? 'BROWSER' : 'INCOGNITO'} />}

              {isSupportedDevice && (
              <div className={wrapperClassNames}>
                <Main
                  myinterviewRef={myinterviewRef}
                  widgetConfig={{
                    config: {
                      ...config,
                      onFinish: onInterviewCompleted,
                      onError,
                      practiceQuestions: convertQuestions(config.practiceQuestions),
                    },
                    job: {
                      ...job,
                      questions: convertQuestions(job.questions),
                    },
                    video: {
                      ...video,
                      referrer: video?.referrer || window.location.href,
                      widget_version,
                    },
                    candidate,
                  }}
                  setShouldShowWaterMark={setShouldShowWaterMark}
                  isWidgetMinimized={isWidgetMinimized}
                  onMinimize={onMinimize}
                />
              </div>
              )}
            </div>
            )}
          </div>
        </IntercomProvider>
      </ErrorBoundary>
    </WrapperComponent>
  );
});
